import React from 'react'
import T from 'prop-types'

const detailsInitiallyExpanded = process.env.NODE_ENV === 'development'

class ErrorBoundary extends React.Component {
  static displayName = "ErrorBoundary"

  static propTypes = {
    children: T.node,
    onErrorCaught: T.func
  }

  static contextTypes = {
    version: T.string,
    onErrorCaught: T.func
  }

  state = {
    caughtError: false,
    error: null,
    info: null,
    detailsExpanded: detailsInitiallyExpanded
  }

  componentDidCatch(error, info) {
    const {
      onErrorCaught
    } = this.props
    this.setState({
      caughtError: true,
      error,
      info
    })
    if (onErrorCaught) {
      onErrorCaught(error, info)
    }
    if (this.context.onErrorCaught) {
      this.context.onErrorCaught(error, info)
    }
  }

  _handleDetailsToggle = () => {
    this.setState({
      detailsExpanded: !this.state.detailsExpanded
    })
  }

  _handleReloadClick = () => {
    window.location.reload()
  }

  _handleHomeClick = () => {
    window.location = `//${window.location.host}`
  }

  _handleCopy = () => {
    const el = document.createElement('textarea') // Textarea so newlines are properly handled
    el.value = this._getErrorDump()
    el.style.position = 'fixed'
    el.style.top = '-999px'
    document.body.appendChild(el)
    el.select()
    try {
      document.execCommand('copy')
    } catch(err) {
      console.log('Copy to clipboard failed')
    }
    document.body.removeChild(el)
  }

  _getErrorDump = () => {
    const {
      error,
      info
    } = this.state

    return [
      `Time:      ${ new Date().toISOString() }`,
      `URL:       ${ window.location.href }`,
      `Version:   ${ this.context.version || 'unknown' }`,
      '',
      error.stack || 'Unknown error stack',
      '',
      'Component stack:',
      info.componentStack || 'Unknown',
    ].join('\r\n')
  }

  render() {
    const {
      children
    } = this.props
    const {
      caughtError,
      detailsExpanded
    } = this.state
    if (caughtError) {
      return (
        <div className="error_boundary">

          <h2><span className="error_icon" /> Whoops, looks like we hit a snag</h2>
          <h6>The visualizer has encountered an error</h6>
          <div>
            <button
              className={`btn btn-default`}
              onClick={this._handleReloadClick}
            ><span className="icon icon-loop2" /> Reload</button>
            <button
              className={`btn btn-default`}
              onClick={this._handleHomeClick}
            ><span className="icon icon-undo" /> Home</button>
            <div className="btn-group">
              <button
                className={`btn btn-warning`}
                onClick={this._handleDetailsToggle}
              ><span className={`icon icon-${ detailsExpanded ? 'minus' : 'plus' }`} /> {detailsExpanded ? 'Hide' : 'Show'} error details</button>
              <button
                className={`btn btn-warning`}
                data-tooltip={`Copy error to clipboard`}
                onClick={this._handleCopy}
              ><span className={`icon icon-signup`} /></button>
            </div>
          </div>
          <div className="error_details">
            { detailsExpanded && (
              <pre className="fade_in_up">
                {this._getErrorDump()}
              </pre>
            ) }
          </div>
        </div>
      )
    }
    return children
  }
}

export default ErrorBoundary


/**
 * Higher-order component wrapper for <ErrorBoundary/> - this can be more ergonomic than
 * the React parent element version when wrapping a whole component.
 */
export function ErrorBoundaryHOC(TargetCmp) {
  const Wrapper = props =>
    <ErrorBoundary>
      <TargetCmp {...props} />
    </ErrorBoundary>
  Wrapper.displayName = `ErrorBoundary(${TargetCmp.displayName || 'Component'})`
  return Wrapper
}
